import { TeoError } from '../../errors/TeoError';
import {
  type AdditionalProps,
  type AiSuggestionV2Data,
  type AiSuggestionV2Request,
  type APITeoMarker,
  type ExcerptContext,
  type SuggestionPreset,
} from '../../TeoApi';
import { TeoEventTarget } from '../../utility/TeoEventTarget';
import type { TeoText } from '../TeoText';
import type { AIHandler } from './AIHandler';
import {
  getSuggestionPromptsByDimension,
  type AISuggestionPrompt,
  type MarkerType,
} from './aiSuggestionPresets';

export type AISuggestion = {
  context: ExcerptContext;
  markerId: string;
  tpi: number;
  replacement: string;
  hidden?: boolean;
  error?: boolean;
};

export type SelectionSuggestionHandlerEvents = {
  modeChanged: (detail: AISuggestionPrompt | null) => void;
  aiSuggestionReceived: (detail: AISuggestion) => void;
  suggestionRemoved: (detail: AISuggestion) => void;
  loading: (detail: boolean) => void;
};

/**
 * Suggestions for the marker the user picks in the editor.
 *
 * While enabled, the markers a prompt works on are made clickable, and clicking one asks
 * the API to improve exactly that excerpt. Which prompts can be offered depends on the
 * markers the current analysis found, which is why the prompt list lives here too.
 */
export class SelectionSuggestionHandler {
  public events = new TeoEventTarget<SelectionSuggestionHandlerEvents>();
  private _currentPromptList: AISuggestionPrompt[] = [];
  private activePrompt: AISuggestionPrompt | null = null;
  // TODO@Leonard this was private, maybe it should be protected and base of BrandfitHandler?
  public activeExcerpt?: ExcerptContext | null;
  private additionalProps: AdditionalProps | undefined;
  private _activeSuggestions: AISuggestion[] = [];
  private _isLoading = false;
  private _otherMarkersAllowed = true;
  public lastSuggestionDebug: AiSuggestionV2Data | null = null;
  private _ignoreMarkerEvents = false;
  private markerClickRemover: (() => void) | null = null;
  private alwaysAllowedMarkers: string[] = ['editor_diff'];

  constructor(
    private text: TeoText,
    private ai: AIHandler,
  ) {
    this.text.ui.events.addEventListener('markers', (markers) => {
      if (this._ignoreMarkerEvents) return;
      // this._ignoreMarkerEvents = true;
      // TODO@ioss making this typed needs a lot of refactoring!
      if (!this.checkMarkers(markers as MarkerType[])) {
        console.warn('Markers are not compatible with the active AI prompt.');
        this.disable(false);
      } else if (this.activePrompt) {
        this.text.markers.addMarkers([], this.getPromptMarkers(this.activePrompt), true);
      }
    });
    this.text.store.addEventListener('view', () => {
      this.disable();
    });
  }

  get activeSuggestions(): AISuggestion[] {
    return this._activeSuggestions;
  }

  /**
   * Drops one suggestion from the list the card is showing.
   * A new array is published so listeners that keep their own copy pick up the change.
   */
  removeSuggestion(index: number): void {
    const suggestion = this._activeSuggestions[index];
    if (!suggestion) return;
    this._activeSuggestions = this._activeSuggestions.filter((_, i) => i !== index);
    this.events.triggerEvent('suggestionRemoved', suggestion);
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  get isEnabled(): boolean {
    return this.activePrompt !== null;
  }

  get currentPromptList() {
    return this._currentPromptList;
  }

  get currentPrompt(): AISuggestionPrompt | null {
    return this.activePrompt;
  }

  set isLoading(value: boolean) {
    if (this._isLoading === value) return;
    this._isLoading = value;
    this.events.triggerEvent('loading', value);
  }

  /**
   * Checks if the provided markers are compatible with the active AI prompt.
   * @param markers The markers to check.
   * @returns True if the markers are compatible, false otherwise.
   */
  private checkMarkers(markers: MarkerType[]): boolean {
    if (!this.activePrompt || this._otherMarkersAllowed) return true;

    const allMarkers = [...this.activePrompt.markers, ...this.alwaysAllowedMarkers];
    return markers.every((marker) => {
      return allMarkers.includes(marker);
    });
  }

  updatePromptList(): AISuggestionPrompt[] {
    const dimension = this.ai.currentDimension;
    this._currentPromptList = getSuggestionPromptsByDimension(dimension);
    if (dimension && this._currentPromptList.length === 0) {
      console.warn('No prompts found for dimension:', dimension);
    }
    this._currentPromptList.forEach((prompt) => {
      if (prompt.available === undefined || prompt.available === true) {
        prompt.available = this.checkPromptAvailability(prompt);
      }
    });
    return this._currentPromptList;
  }

  checkPromptAvailability(prompt: AISuggestionPrompt): boolean {
    if (!this.text.analysis) {
      console.warn('Text analysis is not available. Cannot check prompt availability.');
      return false;
    }
    const allowOtherMarkers = prompt.allowOtherMarkers;
    const markers = this.text.analysis.getMarkers();
    if (!allowOtherMarkers) {
      const requiredMarkers = this.getPromptMarkers(prompt);
      return requiredMarkers.some((marker) => markers.some((m) => m.marker == marker));
    }
    return true;
  }

  /**
   * Makes the markers of a prompt clickable, so a suggestion can be requested for one of them.
   * @param prompt The AI prompt to activate.
   */
  enable(prompt: AISuggestionPrompt): void {
    if (this.activePrompt) {
      this.disable();
    }
    this.text.diff.update(this.text.text, true);
    this._otherMarkersAllowed = !!prompt.allowOtherMarkers;
    this._ignoreMarkerEvents = true;
    if (!this._otherMarkersAllowed) {
      this.text.markers.setMarkers([], this.getPromptMarkers(prompt));
    } else {
      this.text.markers.addMarkers([], this.getPromptMarkers(prompt), true);
    }

    setTimeout(() => {
      this._ignoreMarkerEvents = false;
    }, 100); // Allow some time for the markers to be set before enabling marker events

    this.activePrompt = prompt;

    this.markerClickRemover = this.text.markers.events.addEventListener(
      'markerClicked',
      ((detail: { markerId: string; marker: APITeoMarker }) => {
        this.handleMarkerClick(detail);
      }).bind(this),
    );
    this.events.triggerEvent('modeChanged', prompt);
  }

  /**
   * Stops listening for marker clicks and drops the current selection.
   */
  disable(removeMarkers = true): void {
    if (!this.activePrompt) return;

    if (this.markerClickRemover) {
      this.markerClickRemover();
      this.markerClickRemover = null;
    }

    if (removeMarkers) {
      this.text.markers.removeMarkers(this.getPromptMarkers(this.activePrompt));
    }
    this.activePrompt = null;
    this.activeExcerpt = null;
    this._activeSuggestions = [];
    this._otherMarkersAllowed = true;
    this.text.diff.hide();
    this.text.markers.focusMarkers([]);
    this.events.triggerEvent('modeChanged', null);

    console.log('AI mode disabled.');
  }

  async resendActive(modifier: string): Promise<void> {
    if (!this.activePrompt) {
      console.warn('No active AI prompt to resend request.');
      return;
    }
    if (!this.activeExcerpt) {
      console.warn('No active excerpt context to resend request.');
      return;
    }
    console.log('Resending AI request...', 'Unused modifier:', modifier);
    this.isLoading = true;
    this.handleAiSuggestionV2(await this.getAiSuggestionV2());
  }

  /**
   * Handles marker clicks while enabled.
   * Sends an AI suggestion request with the context of the clicked marker.
   * @param detail The marker click details.
   */
  private async handleMarkerClick(detail: {
    markerId: string;
    marker: APITeoMarker;
  }): Promise<void> {
    const { markerId } = detail;
    this.activeExcerpt = this.text.markers.createMarkerContext(
      markerId,
      this.activePrompt?.contextMarker,
    );
    if (!this.activeExcerpt) {
      console.warn('No marker context available.');
      return;
    }
    console.log(
      `Marker clicked: ${markerId}. Sending AI suggestion request...`,
      this.activeExcerpt,
    );
    this.text.markers.focusMarkers([markerId]);
    this._activeSuggestions = [];
    this.handleAiSuggestionV2(await this.getAiSuggestionV2());
  }

  private getAiRequestV2(): AiSuggestionV2Request {
    if (!this.activePrompt || !this.activeExcerpt) {
      throw new Error('No active AI prompt or excerpt context available.');
    }

    const requestBase = this.ai.getRequestBase();

    console.info('data available for AI suggestion:', {
      additionalProps: this.additionalProps,
      tool: this.activePrompt.type,
      tool_id: this.activePrompt.id,
    });

    const req: AiSuggestionV2Request = {
      context: this.activeExcerpt,
      ...requestBase,
      preset: this.activePrompt.type as SuggestionPreset,
    };

    return req;
  }

  private async getAiSuggestionV2(): Promise<AiSuggestionV2Data> {
    this.isLoading = true;
    try {
      const aiRequestV2 = this.getAiRequestV2();
      const suggestion = await this.text.core.api.getAiSuggestionV2(aiRequestV2);
      this.isLoading = false;
      console.log('AI suggestion received:', suggestion);
      if (TeoError.isError(suggestion)) {
        throw new Error(`API error: ${suggestion.message}`);
      }
      console.info('AI suggestion V2 received:', suggestion);
      return suggestion;
    } catch (error) {
      console.error('Failed to get AI suggestion:', error);
      this.text.core.notifications.addError(error);
      this.isLoading = false;
      throw error;
    }
  }

  private getPromptMarkers(prompt: AISuggestionPrompt): string[] {
    const markers = prompt.markers;
    if (!markers) {
      console.warn(`No markers found for prompt type: ${prompt.type}`);
      return ['paragraph'];
    }
    return markers;
  }

  /**
   * Handles the AI suggestion response.
   * @param suggestion The AI suggestion data.
   */
  public handleAiSuggestionV2(suggestion: AiSuggestionV2Data): void {
    this.lastSuggestionDebug = suggestion;
    const aiSuggestion = this.createAiSuggestionV2(suggestion);
    this._activeSuggestions.push(aiSuggestion);
    this.events.triggerEvent('aiSuggestionReceived', aiSuggestion);
  }

  private createAiSuggestionV2(rawSuggestion: AiSuggestionV2Data): AISuggestion {
    if (!rawSuggestion) {
      throw new Error('Invalid AI suggestion data received.');
    }
    if (!this.activeExcerpt) {
      throw new Error('No active excerpt context available.');
    }

    if (rawSuggestion.suggestions.length === 0) {
      throw new Error('No suggestions found in the AI suggestion data.');
    }
    const tpi =
      rawSuggestion.suggestions[0]!.result.tpi.percentage -
      rawSuggestion.base_result.tpi.percentage;
    return {
      replacement: rawSuggestion.suggestions[0]!.text,
      tpi,
      markerId: this.activeExcerpt.replacementWrapper,
      context: this.activeExcerpt,
      hidden: tpi < 0,
    };
  }
}
